import { injectable, inject } from 'inversify';
import * as lsp from 'vscode-languageserver';
import { TextDocument, convertChangesRange } from '@workbench-stack/language-core';
import { GoCompletionItemProvider } from './go-suggest';
import { Hover, Location, uriToStringUri, SymbolInformation, TextEdit } from './util';
import { definitionLocation } from './go-declaration';
import { GoReferenceProvider } from './go-references';
import { GoDocumentFormattingEditProvider } from './go-format';
import { GoDocumentSymbolProvider } from './go-outline';
import { GoDefinitionProvider } from './go-declaration';
import { TextDocuments } from 'vscode-languageserver';
import * as ILogger from 'bunyan';

@injectable()
export class GoService {

    public readonly documents = new TextDocuments();


    private initializeResult;
    private tspClient;
    private openedDocumentUris: Map<string, any> = new Map<string, any>();
    private diagnosticQueue;
    private completionItemProvider: GoCompletionItemProvider;
    private referenceProvider: GoReferenceProvider;
    private formattingProvider: GoDocumentFormattingEditProvider;
    private documentSymbolProvider: GoDocumentSymbolProvider;
    private definitionProvider: GoDefinitionProvider;
    private logger: ILogger;
    private activated: () => void;
    private activation = new Promise<void>((resolve, reject) => {
        this.activated = resolve;
    });


    constructor(
        @inject('Logger')
        logger: ILogger,
    ) {
        this.logger = logger.child({ className: GoService });
        this.completionItemProvider = new GoCompletionItemProvider();
        this.referenceProvider = new GoReferenceProvider();
        this.formattingProvider = new GoDocumentFormattingEditProvider();
        this.documentSymbolProvider = new GoDocumentSymbolProvider();
        this.definitionProvider = new GoDefinitionProvider();

        this.activated();
    }

    public onInitialize = () => {
        this.logger.debug('onInitialize  go');
        return {
            capabilities: {
                textDocumentSync: this.documents.syncKind,
                completionProvider: true,
                definitionProvider: true,
                documentFormattingProvider: true,
                documentSymbolProvider: true,
                hoverProvider: true,
                referencesProvider: true,
                workspaceSymbolProvider: false,
                documentHighlightProvider: false,
                documentLinkProvider: false,
                renameProvider: false,
            },
        };
    }

    public onDidOpenTextDocument = async params => {
        await this.activation;

        const existDoc = this.openedDocumentUris.get(params.textDocument.uri);
        if (existDoc) { this.openedDocumentUris.delete(params.textDocument.uri); }

        const document = new TextDocument(params.textDocument);
        this.openedDocumentUris.set(params.textDocument.uri, document);
    }

    public onDidChangeTextDocument = async params => {
        await this.activation;
        const document = this.getOpenDocument(params.textDocument.uri);
        const changes = convertChangesRange(params.contentChanges);
        document.apply(changes, params.textDocument.version);
    }

    public onDidCloseTextDocument = async (params) => {
        await this.activation;
        this.openedDocumentUris.delete(params.textDocument.uri);
    }

    public onCompletion = async params => {
        this.logger.debug('onCompletion go');
        await this.activation;
        const document = this.getOpenDocument(params.textDocument.uri);
        const items = await this.completionItemProvider.provideCompletionItems(document, params.position);
        return {
            isIncomplete: false,
            items: items,
        };
    }

    public onDefinition = async (params): Promise<any> => {
        this.logger.debug('onDefinition go', params);
        await this.activation;
        const document = this.getOpenDocument(params.textDocument.uri);
        const definition = await this.definitionProvider.provideDefinition(document, params.position);
        if (definition) {
            return definition;
        } else {
            return [];
        }
    }

    public onDocumentFormatting = async params => {
        await this.activation;
        const document = this.getOpenDocument(params.textDocument.uri);
        return this.formattingProvider.provideDocumentFormattingEdits(document, params.options);
    }


    public onDocumentSymbol = async params => {
        this.logger.debug('onDocumentSymbol go');
        await this.activation;
        const document = this.getOpenDocument(params.textDocument.uri);
        const symbolInformation = await this.documentSymbolProvider.provideDocumentSymbols(document);
        symbolInformation.forEach(symbol => {
            symbol.location = new Location(symbol.range, uriToStringUri(document.uri));
        });
        return symbolInformation;
    }

    public onHover = async params => {
        this.logger.debug('onHover go');
        await this.activation;
        const document = this.getOpenDocument(params.textDocument.uri);
        const definition = await definitionLocation(document, params.position, true).then(definitionInfo => {
            if (definitionInfo === null) { return null; }
            let lines = definitionInfo.declarationlines
                .filter(line => {
                    return !line.startsWith('\t//') && line !== '';
                })
                .map(line => line.replace(/\t/g, '    '));
            let text;
            text = lines.join('\n').replace(/\n+$/, '');
            let hoverTexts = [];
            hoverTexts.push({ language: 'go', value: text });
            if (definitionInfo.doc != null) {
                hoverTexts.push(definitionInfo.doc);
            }
            return new Hover(hoverTexts);
        }, () => {
            return null;
        });

        return definition;
    }

    public onReferences = async (params): Promise<lsp.Location[]> => {
        await this.activation;
        const document = this.getOpenDocument(params.textDocument.uri);
        this.logger.debug('params.position go', params.position);
        return await this.referenceProvider.provideReferences(document, params.position, { includeDeclaration: true });
    }

    private getOpenDocument(uri: string): TextDocument {
        const doc = this.openedDocumentUris.get(uri);
        if (doc) {
            return doc;
        } else {
            throw new Error('Document ' + uri + ' has not been opened.');
        }
    }

}
